import {
  ButtonIcon,
  Icon,
  type IconKind,
  Menu,
  MenuItem,
  type MenuOption,
  Tooltip,
} from "@saviynt/design-system";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";

import TicketErrorModal from "../Modals/TicketErrorModal";
import TicketingSystemModal from "../Modals/TicketingSystemModal";
import {
  FindingsActionType,
  PolicyFindingDetail,
} from "@repo/api/schemas/policiesFindings.schema";
import { toCamelCase, transformCamelCaseToTitleCase } from "@repo/mfe-core";
import {
  ACTION_CREATE,
  ACTION_DELETE,
  ACTION_DISABLEACCOUNT,
} from "../../../constants/policies.constants";
import AssignOwnerModal from "../../PoliciesForm/InventorySidePanel/SummaryTab/components/Ownership/components/AssignOwnerModal/AssignOwnerModal";
import BusinessJustificationModal from "../BusinessJustificationModal/BusinessJustificationModal";
import { useAlert } from "../context/AlertContext";
import { parseError } from "../utils";
import { useFindingsCreateTicket } from "../../../hooks/usePolicyFindings";

interface ActionButtonProps {
  rowData: PolicyFindingDetail;
}

interface FindingsKebabMenu {
  label: string;
  value: string;
  suffixIcon?: string;
  isDisabled?: boolean;
  dataTestId?: string;
  tooltip?: {
    isShow: boolean;
    value: string;
  };
}

export const DUPLICATE_TICKET_ERROR = "ticket_already_present";

export default function ActionButton({ rowData }: ActionButtonProps) {
  const intl = useIntl();
  const [isOnboardTicketingSytem, setIsOnboardTicketingSytem] = useState(false);
  const [isTicketError, setIsTicketError] = useState(false);
  const [isAssingOwner, setIsAssingOwner] = useState(false);
  const [errorMessage, setErrorMessage] = useState({
    error_code: "",
    message: "",
  });
  const [isDeleteDisableModalOpen, setIsDeleteDisableModalOpen] =
    useState(false);
  const [isMenuExpanded, setIsExpanded] = useState(false);
  const hasTriggeredActionRef = useRef(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const actionTypeRef = useRef<FindingsActionType>();
  const { showAlert } = useAlert();
  const {
    showDeleteDisableAccountEnableText,
    violationId,
    allowedActions,
    assetId,
  } = rowData;

  const {
    isSuccess: isCreateSuccess,
    isError: isCreateError,
    error,
    mutate: createTicket,
  } = useFindingsCreateTicket(violationId);

  useEffect(() => {
    if (isCreateSuccess && hasTriggeredActionRef.current) {
      showAlert({ type: ACTION_CREATE });
      hasTriggeredActionRef.current = false;
    }
  }, [isCreateSuccess, showAlert]);

  useEffect(() => {
    if (isCreateError && hasTriggeredActionRef.current) {
      const { message, code = "" } = parseError(error);
      setErrorMessage({
        message,
        error_code: code as string,
      });

      if (code === DUPLICATE_TICKET_ERROR) {
        setIsTicketError(true);
      } else if (code === "INTEGRATION_NOT_ONBOARDED") {
        setIsOnboardTicketingSytem(true);
      }
      hasTriggeredActionRef.current = false;
    }
  }, [error, intl, isCreateError, violationId]);

  const disabledActions = new Set(["delete_account", "disable_account"]);

  const menuOptions =
    allowedActions?.map((action) => {
      return {
        label: transformCamelCaseToTitleCase(toCamelCase(action)),
        value: action,
        ...(showDeleteDisableAccountEnableText &&
          disabledActions.has(action) && {
            isDisabled: true,
            suffixIcon: "LockLocked",
            tooltip: {
              isShow: true,
              value: intl.formatMessage({
                defaultMessage:
                  "This capability is not available during trial. Contact your account representative to know more.",
                id: "9isnbm",
                description:
                  "Tooltip text for disabled actions in NHI findings",
              }),
            },
          }),
        dataTestId: `NHI-Findings-Table-${action.replace(/\s+/g, "")}-MenuItem`,
      };
    }) || [];

  const handleKebabMenuClick = useCallback(
    (selectedOption: MenuOption) => {
      switch (selectedOption.value) {
        case "create_ticket":
          hasTriggeredActionRef.current = true;
          createTicket();
          setIsExpanded(false);
          break;
        case "assign_owner":
          setIsAssingOwner(true);
          setIsExpanded(false);
          break;
        case "delete_account":
          actionTypeRef.current = ACTION_DELETE;
          setIsDeleteDisableModalOpen(true);
          setIsExpanded(false);
          break;
        case "disable_account":
          actionTypeRef.current = ACTION_DISABLEACCOUNT;
          setIsDeleteDisableModalOpen(true);
          setIsExpanded(false);
          break;
      }
    },
    [createTicket],
  );

  const renderCustomMenuItems = useMemo(
    () => (options: FindingsKebabMenu[]) => (
      <div ref={menuRef}>
        {options.map((option, index: number) => {
          const menuItem = (
            <MenuItem
              key={option.value}
              option={option}
              onClick={() => handleKebabMenuClick(option)}
              tabIndex={index}
              suffixIcon={
                option.suffixIcon ? (
                  <Icon
                    kind={option.suffixIcon as IconKind}
                    size="small"
                    color="--color-foreground-neutral-disabled"
                  />
                ) : undefined
              }
              dataTestId={option.dataTestId}
            />
          );

          return option.tooltip?.isShow ? (
            <Tooltip
              key={option.value}
              isPortalEnabled
              className="IdentityCard-tooltip"
              text={option.tooltip.value}
              placement="left-start"
              trigger={menuItem}
            />
          ) : (
            menuItem
          );
        })}
      </div>
    ),
    [handleKebabMenuClick],
  );

  if (allowedActions == null || allowedActions?.length === 0) {
    return null;
  }

  return (
    <>
      {isOnboardTicketingSytem && (
        <TicketingSystemModal
          subtitle={errorMessage.message}
          isOpen={isOnboardTicketingSytem}
          setIsOpen={setIsOnboardTicketingSytem}
        />
      )}
      {isTicketError && (
        <TicketErrorModal
          data={errorMessage}
          isOpen={isTicketError}
          setIsOpen={setIsTicketError}
        />
      )}

      {isAssingOwner && (
        <AssignOwnerModal
          isOpen={isAssingOwner}
          setIsOpen={setIsAssingOwner}
          assetId={assetId}
        />
      )}
      {isDeleteDisableModalOpen && actionTypeRef.current && (
        <BusinessJustificationModal
          actionType={actionTypeRef.current}
          data={rowData}
          isOpen={isDeleteDisableModalOpen}
          setIsOpen={setIsDeleteDisableModalOpen}
          setErrorMessage={setErrorMessage}
          setIsTicketError={setIsTicketError}
          hasTriggeredActionRef={hasTriggeredActionRef}
        />
      )}

      <Menu
        isPortalEnabled
        shouldCloseMenuOnItemClick
        options={menuOptions}
        onChange={handleKebabMenuClick}
        trigger={
          <ButtonIcon
            size="medium"
            kind="ghost"
            ariaLabel={intl.formatMessage({
              defaultMessage: "NHI Detailed Findings Menu Trigger",
              id: "UPNjQZ",
              description: "ARIA label for the NHI Findings action menu button",
            })}
            data-testid="NHI-Detailed-Findings-menu-trigger"
            icon={<Icon kind="MoreVertical" size="medium" />}
          />
        }
        CustomMenuItems={renderCustomMenuItems(menuOptions)}
        isMenuExpanded={isMenuExpanded}
        setIsExpanded={setIsExpanded}
        dataTestId="NHI-Detailed-Findings-kebabMenu"
      />
    </>
  );
}
